// Sugalarm — kiosk native volume hook (cycle 317).
//
// Produces the SAME `VolumeAgentState` shape as the Pi loopback
// `useVolumeAgent`, but backed by the native `KioskVolume` plugin (STREAM_MUSIC
// on the Android tablet kiosk). KioskFrame prefers this when it's available so
// the header's existing volume button + popover light up on the tablet with
// no header/popover change. `available` is false on every non-tablet-APK
// surface (the plugin is absent), so the tablet is the only place this drives
// the control.
//
// Cycle 332: `setMuted` is now real (was a no-op stub) — see the function
// below for how "mute" is synthesized on top of the floored `setVolume`.

import { useCallback, useEffect, useRef, useState } from "react";

import type { VolumeAgentState } from "./useVolumeAgent";
import {
  getVolume as nativeGetVolume,
  isAvailable as nativeVolumeAvailable,
  setVolume as nativeSetVolume,
} from "../lib/kioskVolume";
import type { FrameVariant } from "./useFrameVariant";

const DEBOUNCE_MS = 150;
// Cycle 332: matches the native `KioskVolumePlugin.setVolume` alarm-safety
// floor verbatim (cycle 317 — "the in-app slider can never silence a
// glucose kiosk"). "Mute" on the tablet settles here, never at 0 — the
// native SET path would clamp any lower request right back up to this
// value anyway, so this constant only makes the settle point explicit
// and deterministic rather than relying on implicit clamping.
const TABLET_VOLUME_FLOOR = 20;

export function useKioskVolume(variant: FrameVariant): VolumeAgentState {
  const supported = variant === "kiosk" && nativeVolumeAvailable();
  const [available, setAvailable] = useState(false);
  const [state, setState] = useState<{ level: number; muted: boolean } | null>(
    null,
  );
  const debounceRef = useRef<number | null>(null);
  const pendingLevelRef = useRef<number | null>(null);
  const lastConfirmedRef = useRef<{ level: number; muted: boolean } | null>(
    null,
  );
  // Cycle 332: the level to restore on unmute — remembered client-side
  // since the native plugin has no real mute flag (only `level`).
  const preMuteLevelRef = useRef<number | null>(null);

  const apply = useCallback((next: { level: number; muted: boolean }) => {
    const slim = { level: next.level, muted: next.muted };
    setState(slim);
    lastConfirmedRef.current = slim;
    setAvailable(true);
  }, []);

  const refresh = useCallback(async () => {
    if (!supported) {
      setAvailable(false);
      setState(null);
      return;
    }
    const r = await nativeGetVolume();
    // level < 0 means the native read failed — treat as unavailable so the
    // button hides rather than showing a bogus 0%.
    if (r != null && r.level >= 0) {
      apply({ level: r.level, muted: r.muted });
    } else {
      setAvailable(false);
      setState(null);
    }
  }, [supported, apply]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    return () => {
      if (debounceRef.current != null) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, []);

  const setLevel = useCallback(
    async (n: number) => {
      const clamped = Math.max(0, Math.min(100, Math.round(n)));
      // Optimistic — the slider tracks the finger before the native call
      // confirms (the native side floors it; the confirmed value snaps up).
      setState((prev) =>
        prev ? { ...prev, level: clamped } : { level: clamped, muted: false },
      );
      pendingLevelRef.current = clamped;
      if (debounceRef.current != null) window.clearTimeout(debounceRef.current);
      await new Promise<void>((resolve) => {
        debounceRef.current = window.setTimeout(async () => {
          debounceRef.current = null;
          const target = pendingLevelRef.current;
          pendingLevelRef.current = null;
          if (target == null) {
            resolve();
            return;
          }
          const r = await nativeSetVolume(target);
          if (r != null && r.level >= 0) {
            apply({ level: r.level, muted: r.muted });
          } else if (lastConfirmedRef.current) {
            setState(lastConfirmedRef.current);
          }
          resolve();
        }, DEBOUNCE_MS);
      });
    },
    [apply],
  );

  // Cycle 332: long-press-to-mute on the header volume button. The tablet
  // has no NATIVE mute (a glucose-alarm kiosk must stay audible — cycle
  // 317's floor), so "mute" here means "drop to the floor and remember
  // the prior level," and "unmute" restores it — driven entirely through
  // the same floored `setVolume` the slider already uses, so the
  // alarm-safety floor is enforced identically either way (native-side,
  // regardless of what this hook requests). The popover's Mute button
  // stays hidden on the tablet (`hideMute`); this is reached only via the
  // header button's long-press gesture, which is deliberate and hard to
  // trigger by accident.
  const setMuted = useCallback(
    async (m: boolean) => {
      if (!supported) return;
      if (m) {
        if (!state?.muted) {
          preMuteLevelRef.current = state?.level ?? TABLET_VOLUME_FLOOR;
        }
        setState((prev) =>
          prev ? { ...prev, muted: true } : { level: TABLET_VOLUME_FLOOR, muted: true },
        );
        const r = await nativeSetVolume(TABLET_VOLUME_FLOOR);
        if (r != null && r.level >= 0) {
          apply({ level: r.level, muted: true });
        } else if (lastConfirmedRef.current) {
          setState(lastConfirmedRef.current);
        }
        return;
      }
      const restoreTo = preMuteLevelRef.current ?? TABLET_VOLUME_FLOOR;
      preMuteLevelRef.current = null;
      setState((prev) =>
        prev ? { ...prev, muted: false } : { level: restoreTo, muted: false },
      );
      const r = await nativeSetVolume(restoreTo);
      if (r != null && r.level >= 0) {
        apply({ level: r.level, muted: false });
      } else if (lastConfirmedRef.current) {
        setState(lastConfirmedRef.current);
      }
    },
    [supported, state, apply],
  );

  return { available, state, setLevel, setMuted, refresh };
}
